import { useState } from 'react';
import { z } from 'zod';
import { candidateSchema, jobSchema } from '../../../lib/validation';
import { AnalysisInput } from '../../analysis/hooks/useAnalysis';
import { useAnalysis } from '../../analysis/hooks/useAnalysis';

const uploadFormSchema = z.object({
  candidateName: candidateSchema.shape.candidateName,
  cvText: candidateSchema.shape.cvText,
  jobTitle: jobSchema.shape.jobTitle,
  jobDescription: jobSchema.shape.jobDescription,
});

type UploadFormData = z.infer<typeof uploadFormSchema>;

export function useUploadForm() {
  const [values, setValues] = useState<UploadFormData>({
    candidateName: '',
    cvText: '',
    jobTitle: '',
    jobDescription: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { analysis, isLoading, analyze, resetAnalysis } = useAnalysis();

  const handleChange = (field: keyof UploadFormData, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const setFieldValue = (field: keyof UploadFormData, value: string) => {
    handleChange(field, value);
  };

  const validate = (): boolean => {
    try {
      uploadFormSchema.parse(values);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path.length > 0) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      try {
        await analyze(toAnalysisInput(values));
      } catch (error) {
        // Error is already handled by useAnalysis hook
        console.error('Form submission error:', error);
      }
    }
  };

  const toAnalysisInput = (vals: UploadFormData): AnalysisInput => {
    return {
      candidateName: vals.candidateName as string,
      cvText: vals.cvText as string,
      jobTitle: vals.jobTitle as string,
      jobDescription: vals.jobDescription as string
    };
  };

  const reset = () => {
    setValues({
      candidateName: '',
      cvText: '',
      jobTitle: '',
      jobDescription: ''
    });
    setErrors({});
  };

  const resetFormAndAnalysis = () => {
    reset();
    resetAnalysis();
  };

  return {
    values,
    errors,
    handleChange,
    setFieldValue,
    validate,
    handleSubmit,
    toAnalysisInput,
    reset,
    resetFormAndAnalysis,
    analysis,
    isLoading
  };
}